import { lastValueFrom } from 'rxjs';

import type { Injector } from '@angular/core';
import { ActivityEntityName, TaskLinkType, TaskStatus } from '@jms/model';
import type { Task } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { SorTaskCountService } from '../services/item.task.count.service';
import type { ContractorBulkCommand, ContractorBulkOutcome } from './contractor.item.delete.bulk.action';
import { resolveContractorBulkLines, runContractorBulkLines, type ContractorBulkLine, type ContractorBulkLineOutcome } from './contractor.item.bulk.tally';

export const CONTRACTOR_ITEM_COMPLETE_BULK_ACTION = 'items.contractor.complete';

/** Completes each line's SOR task as the SOR Details Complete button does; the server's completion gate judges every line. */
export async function handleContractorItemCompleteBulkCommand(args: ContractorBulkCommand, injector: Injector, itemsChanged?: () => void): Promise<ContractorBulkOutcome> {

  if (args?.action !== CONTRACTOR_ITEM_COMPLETE_BULK_ACTION) return { handled: false };

  const lines = resolveContractorBulkLines(args.data);
  if (!lines.length) return { handled: true };

  // Lines waiting for approval are known before the run, so the question says they will be left out.
  const waiting = lines.filter(line => line.pending).length;
  const eligible = lines.length - waiting;
  if (eligible) {
    const question = `Mark ${eligible} SOR${eligible === 1 ? '' : 's'} as complete?${waiting ? ` ${waiting} of the ${lines.length} selected can't be completed — they are listed after.` : ''}`;
    const confirmed = await injector.get(ConfirmServicePro).confirm('Mark SOR as Complete', question);
    if (!confirmed) return { handled: true };
  }

  const entityData = injector.get(EntityDataServiceRaw);
  const taskCounts = injector.get(SorTaskCountService);

  await runContractorBulkLines(lines, injector, async line => {
    const task = await resolveOpenSorTask(taskCounts, line);
    if ('kind' in task) return task;

    await lastValueFrom(entityData.patch<Task>(ActivityEntityName.Task, task._id, { status: TaskStatus.Completed }));
    return { kind: 'updated' };
  }, itemsChanged, { noun: 'SOR(s)' });

  return { handled: true, clearSelection: true };
}

/**
 * The line's SOR task while it can still be closed, or why the line is left alone. A pending line has no task the
 * contractor may close until the office approves it (WBS-12406), and a closed task is already where the action goes.
 */
export async function resolveOpenSorTask(taskCounts: SorTaskCountService, line: ContractorBulkLine): Promise<Task | ContractorBulkLineOutcome> {

  // No line name in a reason: the report lists the names after it, one line per reason.
  if (line.pending) return { kind: 'unchanged', reason: 'Waiting for approval' };

  const task = (await taskCounts.getTasks(line.itemId)).find(candidate => candidate?.type === TaskLinkType.Item);
  if (!task?._id) return { kind: 'failed', reason: 'No SOR task' };
  if (task.status !== TaskStatus.Open) return { kind: 'unchanged', reason: `Already ${task.taskStatus?.name ?? task.status}` };

  return task;
}
